import * as React from "react";
import { cn } from "./cn";

type ButtonVariant = "default" | "primary" | "danger" | "ghost" | "secondary";
type ButtonSize = "xs" | "sm" | "md" | "lg";

const sizeClasses: Record<ButtonSize, string> = {
  xs: "h-6 max-h-6 px-2 font-normal text-ui-chrome rounded-[5px]",
  sm: "box-border h-[var(--yard-ui-h-sm)] min-h-[var(--yard-ui-h-sm)] max-h-[var(--yard-ui-h-sm)] shrink-0 px-2.5 py-0 text-ui-chrome leading-none rounded-md",
  md: "box-border h-[var(--yard-ui-h-md)] min-h-[var(--yard-ui-h-md)] max-h-[var(--yard-ui-h-md)] shrink-0 px-3 py-0 text-ui-base leading-none rounded-lg",
  lg: "box-border h-[var(--plm-control-min-height)] min-h-[var(--plm-control-min-height)] max-h-[var(--plm-control-min-height)] shrink-0 px-3 py-0 text-ui-dense leading-none rounded-lg",
};

const variantClasses: Record<ButtonVariant, string> = {
  default:
    "border border-primary bg-primary text-primary-foreground hover:opacity-90",
  secondary:
    "border border-border-strong bg-surface text-secondary shadow-subtle hover:bg-elevated hover:text-foreground",
  primary:
    "border border-transparent bg-[var(--brand-navy)] text-[var(--accent-text)] disabled:opacity-100 disabled:bg-[color-mix(in_srgb,var(--brand-navy)_40%,var(--bg-elevated))] disabled:text-[color-mix(in_srgb,var(--accent-text)_50%,var(--text-muted))]",
  danger:
    "border border-(--status-error) bg-transparent text-(--color-status-error) hover:opacity-80",
  ghost: "border-none bg-transparent text-foreground hover:opacity-80",
};

export function Button({
  className,
  variant = "secondary",
  size = "sm",
  loading = false,
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}) {
  const busy = Boolean(loading);
  return (
    <button
      type="button"
      disabled={Boolean(disabled) || busy}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-1 font-semibold transition-transform duration-100 ease-out active:scale-[0.97] disabled:active:scale-100 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer",
        busy && "cursor-wait disabled:cursor-wait disabled:opacity-70",
        sizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...props}
    />
  );
}

type IconButtonSize = "xs" | "sm" | "md" | "xl";

const iconSizeClasses: Record<IconButtonSize, string> = {
  xs: "box-border size-4 min-w-4 min-h-4 shrink-0 rounded-[5px]",
  sm: "box-border size-[var(--yard-ui-h-sm)] min-w-[var(--yard-ui-h-sm)] min-h-[var(--yard-ui-h-sm)] shrink-0 rounded",
  md: "box-border size-[var(--yard-ui-h-md)] min-w-[var(--yard-ui-h-md)] min-h-[var(--yard-ui-h-md)] shrink-0 rounded-md",
  xl: "size-11 min-w-11 min-h-11 rounded-lg",
};

export const IconButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    size?: IconButtonSize;
    variant?: Exclude<ButtonVariant, "default">;
    loading?: boolean;
  }
>(function IconButton(
  {
    className,
    size = "sm",
    variant = "ghost",
    loading = false,
    disabled,
    ...props
  },
  ref,
) {
  const busy = Boolean(loading);
  return (
    <button
      ref={ref}
      type="button"
      disabled={Boolean(disabled) || busy}
      aria-busy={busy || undefined}
      className={cn(
        "inline-flex items-center justify-center p-0 font-normal transition-transform duration-100 ease-out active:scale-[0.95] disabled:active:scale-100 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer",
        busy && "cursor-wait disabled:cursor-wait disabled:opacity-70",
        iconSizeClasses[size],
        variantClasses[variant],
        className,
      )}
      {...props}
    />
  );
});
IconButton.displayName = "IconButton";
